import {
  Button,
  Card,
  Divider,
  Empty,
  Form,
  Input,
  PageHeader,
  Select,
  Typography,
} from "antd";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { translation } from "../../../i18n/translationConstant";
import {
  createNewGrievance,
  getGrievanceIssuesTypes,
  getGrievanceOfUser,
} from "../../../Services/GrievanceServices/GrievanceService";
import Empty_State from "../../../Assets/empty_state.png";
import Report_Icon from "../../../Assets/report_issue_icon.png";

import "./index.css";
import { defaultPageSize } from "../../../Utils/Constants/apiConstants";
import useINF from "../../Common/Components/InfiniteScroll";
import CommonModal from "../../Common/Components/CommonModal";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

interface GrievanceTicket {
  createdOn: string;
  description: string;
  feedback: string;
  id: string;
  issueType: string;
  status: string;
  vuaId: string;
}

interface GrievanceIssueMap {
  [key: string]: string;
}

const MIN_DESCRIPTION_LENGTH = 5;
const MAX_DESCRIPTION_LENGTH = 1000;

const validateIssueDescription = (
  _: unknown,
  value: string | undefined,
  t: (key: string) => string
): Promise<void> => {
  const description = (value || "").trim();

  // Required and whitespace-only validation.
  // The Form.Item required rule handles empty values.
  if (!description) {
    return Promise.resolve();
  }

  // Enforce length limits.
  if (description.length < MIN_DESCRIPTION_LENGTH) {
    return Promise.reject(
      new Error(t("lblDescriptionTooShort"))
    );
  }

  if (description.length > MAX_DESCRIPTION_LENGTH) {
    return Promise.reject(
      new Error(t("lblDescriptionTooLong"))
    );
  }

  // Reject control characters other than tab and normal line breaks.
  if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/u.test(description)) {
    return Promise.reject(
      new Error(t("lblInvalidDescription"))
    );
  }

  // Reject HTML-like markup.
  if (/<\s*\/?\s*[a-zA-Z][^>]*>|<\s*!\s*--|<\s*\/?\s*\?/u.test(description)) {
    return Promise.reject(
      new Error(t("lblInvalidDescription"))
    );
  }

  // Require at least one Unicode letter or number.
  if (!/[\p{L}\p{N}]/u.test(description)) {
    return Promise.reject(
      new Error(t("lblInvalidDescription"))
    );
  }

  // Reject descriptions consisting only of punctuation or symbols.
  if (/^[\p{P}\p{S}\s]+$/u.test(description)) {
    return Promise.reject(
      new Error(t("lblInvalidDescription"))
    );
  }

  return Promise.resolve();
};


const Grievance: React.FC = () => {
  const { TextArea } = Input;
  const { t } = useTranslation();
  const history = useHistory();

  const [grievance, setGrievance] = useState<GrievanceTicket[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = defaultPageSize;

  const [visible, setVisibility] = useState(false);
  const [grievanceIssues, setGrievanceIssues] =
    useState<GrievanceIssueMap>({});
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [refreshList, setRefreshList] = useState(0);

  const [form] = Form.useForm();

  const [
    isTicketDetailaModalVisible,
    setIsTicketDetailaModalVisible,
  ] = useState(false);

  const [selectedTicket, setSetectedTicket] =
    useState<GrievanceTicket>({
      createdOn: "",
      description: "",
      feedback: "",
      id: "",
      issueType: "",
      status: "",
      vuaId: "",
    });

  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<any>();

  const { newObserver, ref } = useINF(observer, hasMore, setPage);
  observer.current = newObserver.current;

  const { checkFingerprint } = useFingerprintValidation();

  /**
   * Fetch the selected page.
   * Page 1 replaces the existing list; subsequent pages append results.
   */
  const getGrievanceOfUserLocal = useCallback(async () => {
    setListLoading(true);

    try {
      const response = await getGrievanceOfUser(page - 1, pageSize);
      const result = response.data;

      setHasMore(!result.last);

      setGrievance((old) =>
        page === 1 ? result.content : old.concat(result.content)
      );
    } catch (error) {
      console.error("Failed to load grievance tickets:", error);
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    getGrievanceOfUserLocal();
  }, [getGrievanceOfUserLocal, refreshList]);

  useEffect(() => {
    checkFingerprint();
  }, [checkFingerprint]);

  const showModal = () => {
    setVisibility(true);
  };

  const handleCancel = () => {
    if (!submitting) {
      setVisibility(false);
      form.resetFields();
    }
  };

  /**
   * Load issue types when the dropdown is opened.
   */
  const loadGrievanceIssues = async () => {
    if (
      issuesLoading ||
      Object.keys(grievanceIssues).length > 0
    ) {
      return;
    }

    setIssuesLoading(true);

    try {
      const response = await getGrievanceIssuesTypes();
      setGrievanceIssues(response.data || {});
    } catch (error) {
      console.error("Failed to load grievance issue types:", error);
    } finally {
      setIssuesLoading(false);
    }
  };

  /**
   * Ensure that the selected issue type is one of the options
   * returned by the issue-type service.
   */
  const validateIssueType = (
    _: unknown,
    value: string | undefined
  ): Promise<void> => {
    // Let the required rule handle empty values.
    if (!value) {
      return Promise.resolve();
    }

    if (
      !Object.prototype.hasOwnProperty.call(grievanceIssues, value)
    ) {
      return Promise.reject(
        new Error(t("lblInvalidIssueType"))
      );
    }

    return Promise.resolve();
  };

  /**
   * Submit a validated grievance.
   */
  const onDetailsEntered = async (values: {
    issueType: string;
    description: string;
  }) => {
    const description = values.description.trim();
    const issueType = values.issueType;

    // Defensive frontend check before sending the request.
    if (
      !Object.prototype.hasOwnProperty.call(grievanceIssues, issueType)
    ) {
      form.setFields([
        {
          name: "issueType",
          errors: [t("lblInvalidIssueType")],
        },
      ]);
      return;
    }

    setSubmitting(true);

    try {
      await createNewGrievance({
        issueType,
        description,
      });

      setVisibility(false);
      form.resetFields();

      // Reset pagination and trigger a fresh first-page request.
      setGrievance([]);
      setPage(1);
      setHasMore(true);
      setRefreshList((previous) => previous + 1);
    } catch (error) {
      console.error("Failed to create grievance:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const showDetailsModal = (ticket: GrievanceTicket) => {
    setSetectedTicket({
      createdOn: ticket.createdOn,
      description: ticket.description,
      feedback: ticket.feedback,
      id: ticket.id,
      issueType: ticket.issueType,
      status: ticket.status,
      vuaId: ticket.vuaId,
    });

    setIsTicketDetailaModalVisible(true);
  };

  const goBack = () => {
    history.goBack();
  };

  return (
    <div className="grievanceRoot">
      {/* Ticket details modal */}
      <CommonModal
        visible={isTicketDetailaModalVisible}
        onCancel={() => setIsTicketDetailaModalVisible(false)}
      >
        <div className="col alignCenter marginTop40 marginBottom40">
          <Typography.Text className="font20 fontWeight700">
            {selectedTicket.issueType}
          </Typography.Text>

          <div className="row spaceBetween marginTop40 width90">
            <div className="col width45">
              <Typography.Text className="detailsHeader">
                {t("lbldescription")}
              </Typography.Text>

              {/* Render as plain text; do not use dangerouslySetInnerHTML. */}
              <Typography.Text className="detailsBody">
                {selectedTicket.description}
              </Typography.Text>
            </div>

            <div className="col width45">
              <Typography.Text className="detailsHeader">
                {t("lblfeedback")}
              </Typography.Text>

              <Typography.Text className="detailsBody">
                {selectedTicket.feedback
                  ? selectedTicket.feedback
                  : t("lblNoFeedback")}
              </Typography.Text>
            </div>

            <div className="col width45">
              <Typography.Text className="detailsHeader">
                {t("Transaction Id")}
              </Typography.Text>

              <Typography.Text className="detailsBody">
                {selectedTicket.id}
              </Typography.Text>
            </div>

            <div className="col width45">
              <Typography.Text className="detailsHeader">
                {t("Vua ID")}
              </Typography.Text>

              <Typography.Text className="detailsBody">
                {selectedTicket.vuaId}
              </Typography.Text>
            </div>
          </div>

          <div className="col alignCenter">
            <div>
              <Typography.Text className="fontWeight600 font12 marginTop10">
                {moment(selectedTicket.createdOn).format(
                  "DD MMMM YYYY"
                ) +
                  " | " +
                  moment(selectedTicket.createdOn).format("LT")}
              </Typography.Text>
            </div>

            <div className="row">
              <Typography.Text>{t("lblStatus")}</Typography.Text>

              <Typography.Text className="statusType">
                {selectedTicket.status
                  ? selectedTicket.status.charAt(0) +
                    selectedTicket.status.slice(1).toLowerCase()
                  : ""}
              </Typography.Text>
            </div>
          </div>
        </div>
      </CommonModal>

      {/* Create grievance modal */}
      <CommonModal visible={visible} onCancel={handleCancel}>
        <div className="col alignCenter">
          <Typography.Text className="font24 fontWeight600 marginTop10">
            {t("lblReportIssue")}
          </Typography.Text>

          <Form
            form={form}
            name="grievanceForm"
            onFinish={onDetailsEntered}
            autoComplete="off"
            scrollToFirstError
            className="generateTicketForm"
          >
            <Form.Item
              name="issueType"
              rules={[
                {
                  required: true,
                  message: t("lblRequiredField"),
                },
                {
                  validator: validateIssueType,
                },
              ]}
            >
              <Select
                placeholder={t("lblSelectIssueType")}
                className="issueSelector"
                loading={issuesLoading}
                onDropdownVisibleChange={(open) => {
                  if (open) {
                    loadGrievanceIssues();
                  }
                }}
              >
                {Object.keys(grievanceIssues)
                  .sort()
                  .map((key) => (
                    <Select.Option key={key} value={key}>
                      {grievanceIssues[key]}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: t("lblRequiredField"),
                },
                {
                  validator: (_, value) =>
                    validateIssueDescription(_, value, t),
                },
              ]}
            >
              <TextArea
                className="marginTop10 ticketDescription"
                placeholder={t("reportdescription")}
                autoSize={{ minRows: 4, maxRows: 8 }}
                maxLength={MAX_DESCRIPTION_LENGTH}
                showCount
              />
            </Form.Item>

            <div className="row justifyCenter">
              <Button
                className="font16 fontWeight600 ticketsubmit"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
              >
                {t("lblReportIssue")}
              </Button>
            </div>
          </Form>
        </div>
      </CommonModal>

      {/* Page header */}
      <div className="grievanceheader">
        <PageHeader
          className="linkAccountTitle"
          backIcon={<ArrowLeftOutlined className="backIcon" />}
          onBack={goBack}
          extra={
            <Button
              className="reportButton"
              type="primary"
              onClick={showModal}
            >
              {t("lblAddIssue")}
            </Button>
          }
          title={t("lblReportIssue")}
        />
      </div>

      <Divider />

      {/* Ticket list */}
      <div className="grievanceHome">
        {grievance.length > 0 ? (
          <>
            {grievance.map((ticket) => (
              <Card
                key={ticket.id}
                hoverable
                className="cardStyles width100"
                onClick={() => showDetailsModal(ticket)}
              >
                <div className="row spaceBetween alignCenter">
                  <div className="flex40 row alignCenter">
                    <div className="avatarBG size56">
                      <img
                        src={Report_Icon}
                        alt=""
                        className="size32"
                      />
                    </div>

                    <Typography.Text className="fontWeight600 marginLeft10">
                      {t(ticket.issueType)}
                    </Typography.Text>
                  </div>

                  <div className="row spaceBetween flex60">
                    <div>
                      <Typography.Text className="fontWeight600 font12">
                        {t("Transaction ID") + " : "}
                      </Typography.Text>

                      <Typography.Text className="font12">
                        {ticket.id}
                      </Typography.Text>
                    </div>

                    <div>
                      <Typography.Text className="fontWeight600 font12">
                        {moment(ticket.createdOn).format(
                          "DD MMMM YYYY"
                        ) +
                          " | " +
                          moment(ticket.createdOn).format("LT")}
                      </Typography.Text>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {hasMore && <div ref={ref} className="infiniteDiv" />}
          </>
        ) : (
          <Empty
            image={Empty_State}
            description={
              <Typography.Text className="noAccountFound">
                {listLoading
                  ? t("lblLoading")
                  : t(translation.lblIssuesFound)}
              </Typography.Text>
            }
          />
        )}
      </div>
    </div>
  );
};

export default Grievance;